import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import api from '../../../api/axios';
import OrganizationSelect from '../components/OrganizationSelect/OrganizationSelect';

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [organization, setOrganization] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async (orgId = "") => {
    try {
      setLoading(true);

      const url = orgId
        ? `/campaign/list?org_id=${orgId}`
        : "/campaign/list";

      const response = await api.get(url);
      if (response?.data?.status) {
        setCampaigns(response.data.data || []);
      } else {
        setCampaigns([]);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationChange = (e) => {
    const orgId = e.target.value;

    setOrganization(orgId);

    loadCampaigns(orgId);
  };

  const rows = useMemo(() => {
    return campaigns.filter((item) =>
      (item.camp_name || "")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [campaigns, search]);

  return (
    <div className="space-y-6"><div>
      <h2 className="text-2xl font-bold">Campaigns</h2>
      <p className="mt-1 text-sm text-slate-500">Monitor campaigns across all customer organizations.</p>
    </div>
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex gap-3 border-b p-4">

          <label className="relative flex-1">
            <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search campaigns…" className="w-full rounded-lg border px-9 py-2 text-sm" />
          </label>

          <OrganizationSelect
            value={organization}
            onChange={(orgId) => {
              loadCampaigns(orgId);
            }}
            className="w-56"
          />

        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[950px] text-left text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3">Campaign</th>
                <th className="px-5 py-3">Organization</th>
                <th className="px-5 py-3">Start Date</th>
                <th className="px-5 py-3">End Date</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3">Updated</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 bg-white">

              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-12 text-center text-slate-500"
                  >
                    Loading campaigns...
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-12 text-center text-slate-500"
                  >
                    No campaigns found.
                  </td>
                </tr>
              )}

              {!loading &&
                campaigns.map((item) => (
                  <tr
                    key={item.camp_id}
                    className="transition-colors hover:bg-slate-50"
                  >
                    {/* Campaign */}
                    <td className="px-5 py-4">
                      <div>
                        <p className="font-semibold text-slate-800">
                          {item.camp_name}
                        </p>

                        <p className="mt-1 text-xs text-slate-500">
                          {item.camp_id}
                        </p>
                      </div>
                    </td>

                    {/* Organization */}
                    <td className="px-5 py-4">
                      <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
                        {item.org_name}
                      </span>
                    </td>

                    {/* Start */}
                    <td className="px-5 py-4 text-slate-600">
                      {new Date(item.camp_start_date).toLocaleDateString()}
                    </td>

                    {/* End */}
                    <td className="px-5 py-4 text-slate-600">
                      {new Date(item.camp_end_date).toLocaleDateString()}
                    </td>

                    {/* Status */}
                    <td className="px-5 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${item.camp_status === "ACTIVE"
                          ? "bg-green-100 text-green-700"
                          : item.camp_status === "INACTIVE"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                          }`}
                      >
                        {item.camp_status}
                      </span>
                    </td>

                    {/* Created */}
                    <td className="px-5 py-4 text-slate-500">
                      <div>
                        {new Date(item.camp_created_on).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-slate-400">
                        {new Date(item.camp_created_on).toLocaleTimeString()}
                      </div>
                    </td>

                    {/* Updated */}
                    <td className="px-5 py-4 text-slate-500">
                      <div>
                        {new Date(item.camp_updated_on).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-slate-400">
                        {new Date(item.camp_updated_on).toLocaleTimeString()}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
